import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App, Button, Card, Checkbox, Col, DatePicker, Descriptions, Dropdown, Empty, Input, InputNumber, Row,
  Segmented, Select, Space, Statistic, Switch, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd';
import {
  CheckCircleFilled, ClearOutlined, DownloadOutlined, EditOutlined, FileExcelOutlined, FilePdfOutlined,
  InfoCircleOutlined, LockOutlined, ReloadOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { inputNumberMoneyProps } from '../../utils/currency';
import { useAuth } from '../../context/AuthContext';
import { useModuleApi } from '../ortak/moduleApi';
import '../ortak/nfmodules.css';

/**
 * Özel Mülk Satış Rapor (08.10.2026; 13.10.2026 özel mülk + tutar alanları) — routes/yonetim_rapor.py.
 * Yalnız özel mülk lokasyonları (kategori 2, 3). Lokasyon ana satır (en yeni mülk sözleşmesinin
 * tutarı ve ödeme tipi), altında banka sözleşmeleri (sözleşme / fatura tutarı); komisyon tutarı +
 * ödendi, temsil tutarı + geri alınamaz temsil onayı, geri alınamaz onay (onay tutarı = onay anındaki
 * fatura tutarı); aylık / 3 / 6 / 12 aylık genel ve kişi bazlı özet; Excel ve PDF çıktısı.
 * side_menu tablosunda yok; menü öğesi routes/feed.py'de koddan, adres /yonetim-rapor.
 * Yetki `Genel / Yönetim Raporu` (r = görme, w = komisyon/temsil/onay).
 */
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const TARIH_TIPLERI = [
  { value: 'satis', label: 'Satış tarihi' },
  { value: 'fatura', label: 'Fatura tarihi' },
  { value: 'mulk', label: 'Mülk sözleşme tarihi' },
];
// Yeni satış = lokasyon ilk kez imzalanmış; yenilenmiş = lokasyonun daha önce sözleşmesi vardı. Bankaya göre
// değil lokasyonun (mülk sözleşmesinin) satışına göre — services/yonetim_rapor.py _tur_uygula.
const SATIS_TURLERI = [
  { value: 'yeni', label: 'Yeni Satış' },
  { value: 'yenileme', label: 'Yenilenmiş' },
  { value: 'tumu', label: 'Tümü' },
];
const TUR_ACIKLAMA = {
  mulk: 'Lokasyona göre: seçilen aralıktaki mülk sözleşmesi lokasyonun ilk sözleşmesiyse yeni satış, daha önce sözleşmesi varsa yenilenmiş. Lokasyonun bankaları da aynı listeye girer.',
  diger: 'Lokasyona göre: banka satıldığı tarihte yürürlükteki mülk sözleşmesi lokasyonun ilk sözleşmesiyse yeni satış (aynı lokasyona sonradan eklenen bankalar dahil), lokasyonun daha önce sözleşmesi varsa yenilenmiş.',
};
const TurSecimi = ({ tur, setTur, tarihTipi }) => (
  <Space style={{ marginBottom: 12 }} wrap>
    <Segmented size="large" options={SATIS_TURLERI} value={tur} onChange={setTur} />
    <Tooltip title={TUR_ACIKLAMA[tarihTipi === 'mulk' ? 'mulk' : 'diger']}>
      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
    </Tooltip>
  </Space>
);
const PERIYOTLAR = [
  { value: 1, label: 'Aylık' },
  { value: 3, label: '3 Aylık' },
  { value: 6, label: '6 Aylık' },
  { value: 12, label: 'Yıllık' },
];
const TAHSILAT_DURUMLARI = [
  { value: 'tahsil', label: 'Tahsil edildi' },
  { value: 'bekliyor', label: 'Bekliyor' },
  { value: 'kesilmedi', label: 'Fatura kesilmedi' },
  { value: 'yok', label: 'Fatura yok' },
];
const TAHSILAT_RENK = {
  'Tahsil edildi': 'success', Bekliyor: 'warning', 'Fatura kesilmedi': 'processing', 'Fatura yok': 'default',
};

const para = (v) => (v == null ? '' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`);
const tarih = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '');
const buAy = () => [dayjs().startOf('month'), dayjs().endOf('month')];
const hata = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : e?.message || 'İşlem başarısız.';
};

function indir(getAuthHeaders, params, message) {
  const kapat = message.loading('Dosya hazırlanıyor…', 0);
  return axios.get(`${API_BASE_URL}/yonetim-rapor/disa-aktar`, {
    params, headers: getAuthHeaders(), responseType: 'blob',
  }).then((r) => {
    const ad = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || `yonetim-rapor.${params.bicim}`;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = ad;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }).catch(async (e) => {
    let m = hata(e);
    try { m = JSON.parse(await e.response.data.text()).detail || m; } catch { /* blob değil */ }
    message.error(m);
  }).finally(kapat);
}

const CiktiButonu = ({ onIndir }) => (
  <Dropdown
    menu={{
      items: [
        { key: 'xlsx', icon: <FileExcelOutlined style={{ color: '#1d6f42' }} />, label: 'Excel (.xlsx)' },
        { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#c0392b' }} />, label: 'PDF (yatay A4)' },
      ],
      onClick: ({ key }) => onIndir(key),
    }}
  >
    <Button icon={<DownloadOutlined />}>Çıktı al</Button>
  </Dropdown>
);

/** Checkbox'lı, aranabilir çoklu seçim; "görünenleri seç" aramaya uyan seçenekleri ekler. */
function CokluSecim({ placeholder, secenekler, deger, onChange }) {
  const [arama, setArama] = useState('');
  const uyan = (o) => o.label.toLocaleLowerCase('tr').includes(arama.trim().toLocaleLowerCase('tr'));
  const gorunen = secenekler.filter(uyan);
  return (
    <Select
      mode="multiple" allowClear showSearch placeholder={placeholder} style={{ minWidth: 240, maxWidth: 360 }}
      maxTagCount="responsive" value={deger} onChange={onChange} options={secenekler}
      searchValue={arama} onSearch={setArama} autoClearSearchValue={false}
      filterOption={(_, o) => uyan(o)} menuItemSelectedIcon={null} popupMatchSelectWidth={360}
      onDropdownVisibleChange={(a) => { if (!a) setArama(''); }}
      optionRender={(o) => <Space><Checkbox checked={deger.includes(o.value)} />{o.label}</Space>}
      notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Lokasyon yok" />}
      dropdownRender={(menu) => (
        <>
          <Space style={{ padding: '4px 8px' }}>
            <Button size="small" onClick={() => onChange([...new Set([...deger, ...gorunen.map((o) => o.value)])])}>
              {arama ? `Görünenleri seç (${gorunen.length})` : `Tümünü seç (${secenekler.length})`}
            </Button>
            <Button size="small" onClick={() => onChange([])} disabled={!deger.length}>Temizle</Button>
          </Space>
          {menu}
        </>
      )}
    />
  );
}

/** Tıklayınca düzenlenen tutar; Enter / odak kaybında değiştiyse kaydeder (boş = sil, Esc = vazgeç). */
function TutarGirisi({ deger, kilitli, yukleniyor, onKaydet }) {
  const [duzenle, setDuzenle] = useState(false);
  const [val, setVal] = useState(deger);
  if (kilitli) return deger == null ? <Text type="secondary">—</Text> : para(deger);
  if (!duzenle) {
    return (
      <Button type="text" size="small" loading={yukleniyor} style={{ padding: '0 4px' }}
        onClick={() => { setVal(deger); setDuzenle(true); }}>
        {deger == null ? <Text type="secondary"><EditOutlined /> Gir</Text> : para(deger)}
      </Button>
    );
  }
  const kaydet = () => {
    setDuzenle(false);
    if ((val ?? null) !== (deger ?? null)) onKaydet(val ?? null);
  };
  return (
    <InputNumber
      {...inputNumberMoneyProps} autoFocus size="small" min={0} step={0.01} style={{ width: 130 }}
      value={val} onChange={setVal} onPressEnter={kaydet} onBlur={kaydet}
      onKeyDown={(e) => { if (e.key === 'Escape') setDuzenle(false); }}
    />
  );
}

/** Yazma ucunun döndürdüğü alanlar (bsk_id hariç) satıra olduğu gibi işlenir. */
const satirAlanlari = (yanit) => {
  const alanlar = { ...yanit.data };
  delete alanlar.bsk_id;
  return alanlar;
};
const toplamTutar = (liste, alan, kosul = () => true) => liste.filter(kosul).reduce((a, x) => a + (x[alan] || 0), 0);

// --- Liste --------------------------------------------------------------------------------
function ListeSekmesi({ api, sorumlular }) {
  const { modal, message } = App.useApp();
  const { getAuthHeaders } = useAuth();
  const [tarihTipi, setTarihTipi] = useState('satis');
  const [aralik, setAralik] = useState(buAy);
  const [sorumlu, setSorumlu] = useState();
  const [tur, setTur] = useState('yeni');
  const [ara, setAra] = useState('');
  const [tahsilat, setTahsilat] = useState([]);
  const [lokSecim, setLokSecim] = useState([]);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [acik, setAcik] = useState([]);
  const [isleniyor, setIsleniyor] = useState(null);

  const params = useMemo(() => ({
    tarih_tipi: tarihTipi,
    bas: aralik?.[0]?.format('YYYY-MM-DD'),
    bit: aralik?.[1]?.format('YYYY-MM-DD'),
    sorumlu, tur,
    tahsilat: tahsilat.join(',') || undefined,
    lokasyon: lokSecim.join(',') || undefined,
  }), [tarihTipi, aralik, sorumlu, tur, tahsilat, lokSecim]);

  // Tarih değişince lokasyon listesi değişir; eski seçim yeni aralıkta anlamsız kalmasın
  useEffect(() => { setLokSecim([]); }, [tarihTipi, aralik]);

  const yukle = useCallback(() => {
    if (!params.bas || !params.bit) return;
    setYukleniyor(true);
    api.get('/liste', params)
      .then((d) => { setVeri(d); setAcik(d.satirlar.filter((s) => s.children).map((s) => s.key)); })
      .catch((e) => message.error(hata(e)))
      .finally(() => setYukleniyor(false));
  }, [api, params, message]);

  useEffect(() => { yukle(); }, [yukle]);

  const sifirla = () => {
    setTarihTipi('satis'); setAralik(buAy()); setSorumlu(undefined); setTur('yeni');
    setTahsilat([]); setLokSecim([]); setAra('');
  };
  const varsayilanda = tarihTipi === 'satis' && !sorumlu && tur === 'yeni' && !tahsilat.length && !lokSecim.length
    && !ara && aralik?.[0]?.isSame(buAy()[0], 'day') && aralik?.[1]?.isSame(buAy()[1], 'day');

  // Bir banka satırını yerinde güncelle; lokasyon ve genel sayaç/toplamları yeniden hesapla
  // (services/yonetim_rapor.py lokasyon_toplamlari / _metrikler ile aynı alanlar)
  const satiriGuncelle = (bskId, alanlar) => setVeri((v) => {
    const ozet = (c) => ({
      komisyon_sayisi: c.filter((x) => x.komisyon_odendi).length,
      onay_sayisi: c.filter((x) => x.onaylandi).length,
      temsil_onay_sayisi: c.filter((x) => x.temsil_onaylandi).length,
      komisyon_tutar: toplamTutar(c, 'komisyon_tutar'),
      temsil_tutar: toplamTutar(c, 'temsil_tutar'),
      onay_tutar: toplamTutar(c, 'onay_tutar', (x) => x.onaylandi),
    });
    const satirlar = v.satirlar.map((l) => {
      if (!l.children?.some((c) => c.bsk_id === bskId)) return l;
      const children = l.children.map((c) => (c.bsk_id === bskId ? { ...c, ...alanlar } : c));
      return { ...l, children, ...ozet(children) };
    });
    const o = ozet(satirlar.flatMap((l) => l.children || []));
    return {
      ...v, satirlar,
      toplam: {
        ...v.toplam,
        komisyon_odenen: o.komisyon_sayisi, onaylanan: o.onay_sayisi, temsil_onaylanan: o.temsil_onay_sayisi,
        komisyon_tutar: o.komisyon_tutar, temsil_tutar: o.temsil_tutar, onay_tutar: o.onay_tutar,
      },
    };
  });

  // PUT /{bsk}/{yol} — dönen alanlar satıra işlenir
  const kaydet = (r, yol, govde, anahtar) => {
    setIsleniyor(`${anahtar}${r.bsk_id}`);
    axios.put(`${API_BASE_URL}/yonetim-rapor/${r.bsk_id}/${yol}`, govde, { headers: getAuthHeaders() })
      .then(({ data }) => satiriGuncelle(r.bsk_id, satirAlanlari(data)))
      .catch((e) => { message.error(hata(e)); if (e?.response?.status === 409) yukle(); })
      .finally(() => setIsleniyor(null));
  };

  const onayla = (r, lokasyon) => {
    modal.confirm({
      title: 'Satışı onayla',
      icon: <SafetyCertificateOutlined style={{ color: '#faad14' }} />,
      width: 560,
      okText: 'Onayla (geri alınamaz)',
      okButtonProps: { danger: true },
      cancelText: 'Vazgeç',
      content: (
        <>
          <Descriptions size="small" column={1} bordered style={{ margin: '12px 0' }}>
            <Descriptions.Item label="Lokasyon">{lokasyon?.kod} · {lokasyon?.ad}</Descriptions.Item>
            <Descriptions.Item label="Banka">{r.ad}{r.tur === 'yenileme' ? ' (Yenilenmiş)' : ''}</Descriptions.Item>
            <Descriptions.Item label="Sözleşme no">{r.kod || '—'}</Descriptions.Item>
            <Descriptions.Item label="Sorumlu">{r.sorumlu_adi || '—'}</Descriptions.Item>
            <Descriptions.Item label="Satış tarihi">{tarih(r.satis_tarihi) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Banka sözleşme tutarı">{para(r.yillik_kira)}</Descriptions.Item>
            <Descriptions.Item label="Fatura">{r.fatura_no ? `${r.fatura_no} · ${tarih(r.fatura_tarih)}` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Tahsilat">{r.tahsilat}</Descriptions.Item>
            <Descriptions.Item label="Komisyon">
              {r.komisyon_tutar == null ? '—' : para(r.komisyon_tutar)} · {r.komisyon_odendi ? 'ödendi' : 'ödenmedi'}
            </Descriptions.Item>
            <Descriptions.Item label="Onay tutarı (fatura tutarı)">
              {r.fatura_tutar == null ? <Text type="danger">Fatura yok — onay tutarı boş kalır</Text> : <Text strong>{para(r.fatura_tutar)}</Text>}
            </Descriptions.Item>
          </Descriptions>
          <Text type="warning">
            <LockOutlined /> Onaylandıktan sonra onay geri alınamaz; onay tutarı ve komisyon değiştirilemez.
          </Text>
        </>
      ),
      onOk: () => axios.post(`${API_BASE_URL}/yonetim-rapor/${r.bsk_id}/onay`, {}, { headers: getAuthHeaders() })
        .then(({ data }) => {
          satiriGuncelle(r.bsk_id, {
            onaylandi: true, onay_tarihi: data.data.onay_tarihi, onaylayan_adi: data.data.onaylayan_adi,
            onay_tutar: data.data.onay_tutar,
          });
          message.success('Onaylandı.');
        })
        .catch((e) => { message.error(hata(e)); if (e?.response?.status === 409) yukle(); }),
    });
  };

  const temsilOnayla = (r, lokasyon) => {
    modal.confirm({
      title: 'Temsili onayla',
      icon: <SafetyCertificateOutlined style={{ color: '#faad14' }} />,
      width: 480,
      okText: 'Onayla (geri alınamaz)',
      okButtonProps: { danger: true },
      cancelText: 'Vazgeç',
      content: (
        <>
          <Descriptions size="small" column={1} bordered style={{ margin: '12px 0' }}>
            <Descriptions.Item label="Lokasyon">{lokasyon?.kod} · {lokasyon?.ad}</Descriptions.Item>
            <Descriptions.Item label="Banka">{r.ad}</Descriptions.Item>
            <Descriptions.Item label="Sorumlu">{r.sorumlu_adi || '—'}</Descriptions.Item>
            <Descriptions.Item label="Temsil tutarı"><Text strong>{para(r.temsil_tutar)}</Text></Descriptions.Item>
          </Descriptions>
          <Text type="warning"><LockOutlined /> Onaylandıktan sonra temsil onayı ve tutarı değiştirilemez.</Text>
        </>
      ),
      onOk: () => axios.post(`${API_BASE_URL}/yonetim-rapor/${r.bsk_id}/temsil-onay`, {}, { headers: getAuthHeaders() })
        .then(({ data }) => {
          satiriGuncelle(r.bsk_id, satirAlanlari(data));
          message.success('Temsil onaylandı.');
        })
        .catch((e) => { message.error(hata(e)); if (e?.response?.status === 409) yukle(); }),
    });
  };

  const yazabilir = Boolean(veri?.yazabilir);
  const onaylayabilir = Boolean(veri?.onaylayabilir); // onay + temsil onayı yalnız bir kişide (routes ONAYLAYAN)
  const onayIpucu = onaylayabilir ? '' : `Yalnız ${veri?.onaylayan_adi || 'yetkili kişi'} onaylayabilir`;

  const gosterilen = useMemo(() => {
    if (!veri) return [];
    const q = ara.trim().toLocaleLowerCase('tr');
    if (!q) return veri.satirlar;
    const uyar = (x) => [x.kod, x.ad, x.sorumlu_adi, x.fatura_no]
      .some((v) => v != null && String(v).toLocaleLowerCase('tr').includes(q));
    return veri.satirlar
      .map((l) => (uyar(l) ? l : { ...l, children: l.children?.filter(uyar) }))
      .filter((l) => uyar(l) || l.children?.length);
  }, [veri, ara]);

  const lokasyonu = (bskKey) => veri?.satirlar.find((l) => l.children?.some((c) => c.key === bskKey));
  const sayac = (a, n) => (n ? <Text type={a === n ? 'success' : 'secondary'}>{a}/{n}</Text> : null);

  const sutunlar = [
    {
      title: 'Kod', dataIndex: 'kod', width: 120,
      render: (v, r) => (r.tip === 'lokasyon' ? <Text strong>{v}</Text> : <Text type="secondary">{v || '—'}</Text>),
    },
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 170, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'Lokasyon / Banka', dataIndex: 'ad', width: 300, ellipsis: true,
      render: (v, r) => (r.tip === 'lokasyon'
        ? <Space size={4}><Text strong>{v}</Text>{r.mulk_tur === 'yenileme' && <Tag color="blue" bordered={false}>Yenilenmiş</Tag>}</Space>
        : v),
    },
    {
      title: 'Mülk Söz. Tarihi', width: 110,
      render: (_, r) => (r.tip === 'lokasyon' && r.mulk_tarihi
        ? <Tooltip title={tarihTipi === 'mulk' ? 'Seçilen aralıktaki mülk (yer sahibi) sözleşmesi' : 'En yeni mülk (yer sahibi) sözleşmesi'}>{tarih(r.mulk_tarihi)}</Tooltip>
        : null),
    },
    {
      title: 'Satış Tarihi', width: 105,
      render: (_, r) => (r.tip === 'lokasyon' ? null
        : <Tooltip title="Banka sözleşmesi başlangıcı">{tarih(r.satis_tarihi)}</Tooltip>),
    },
    {
      title: 'Mülk Söz. Tutarı', width: 170, align: 'right',
      render: (_, r) => (r.tip !== 'lokasyon' ? null : (
        <Space size={4} direction="vertical" style={{ alignItems: 'flex-end' }}>
          <Text strong>{r.mulk_tutar == null ? '—' : para(r.mulk_tutar)}</Text>
          {r.mulk_odeme && <Tag bordered={false} color={r.mulk_odeme.toLocaleLowerCase('tr').includes('girdikçe') ? 'purple' : 'cyan'} style={{ margin: 0 }}>{r.mulk_odeme}</Tag>}
        </Space>
      )),
    },
    {
      title: 'Banka Söz. Tutarı', dataIndex: 'yillik_kira', width: 140, align: 'right',
      render: (v, r) => (r.tip === 'lokasyon' ? <Text strong>{r.children ? para(v) : ''}</Text> : para(v)),
    },
    {
      title: 'Fatura Tarihi', width: 105,
      render: (_, r) => (r.tip === 'lokasyon' ? null : (tarih(r.fatura_tarih) || <Text type="secondary">—</Text>)),
    },
    {
      title: 'Fatura No', width: 130,
      render: (_, r) => {
        if (r.tip === 'lokasyon') return r.banka_sayisi ? sayac(r.fatura_sayisi, r.banka_sayisi) : null;
        if (!r.fatura_no) return <Text type="secondary">—</Text>;
        return (
          <>
            {r.fatura_no}
            {r.fatura_eslesme === 'lokasyon-banka-donem' && (
              <Tooltip title="Fatura sözleşmeye bağlı kaydedilmemiş; lokasyon + banka + dönem başlangıcıyla eşleştirildi.">
                <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: 'Fatura Tutarı', width: 140, align: 'right',
      render: (_, r) => {
        if (r.tip === 'lokasyon') return r.fatura_tutar ? <Text strong>{para(r.fatura_tutar)}</Text> : null;
        if (r.fatura_tutar == null) return <Text type="secondary">—</Text>;
        return r.kesildi ? para(r.fatura_tutar)
          : <Tooltip title="Fatura kesilmedi"><Text type="secondary">{para(r.fatura_tutar)}</Text></Tooltip>;
      },
    },
    {
      title: 'Tahsilat Durum', width: 130,
      render: (_, r) => (r.tip === 'lokasyon'
        ? (r.banka_sayisi ? sayac(r.tahsil_sayisi, r.banka_sayisi) : <Tag>Banka yok</Tag>)
        : <Tooltip title={r.tahsilat_tarihi ? `Tahsilat: ${tarih(r.tahsilat_tarihi)}` : ''}><Tag color={TAHSILAT_RENK[r.tahsilat]}>{r.tahsilat}</Tag></Tooltip>),
    },
    {
      title: 'Komisyon Tutarı', width: 145, align: 'right',
      render: (_, r) => (r.tip === 'lokasyon'
        ? (r.komisyon_tutar ? <Text strong>{para(r.komisyon_tutar)}</Text> : null)
        : <TutarGirisi deger={r.komisyon_tutar} kilitli={!yazabilir || r.onaylandi} yukleniyor={isleniyor === `kt${r.bsk_id}`}
            onKaydet={(tutar) => kaydet(r, 'komisyon', { tutar }, 'kt')} />),
    },
    {
      title: 'Komisyon Ödendi', width: 120, align: 'center',
      render: (_, r) => {
        if (r.tip === 'lokasyon') return sayac(r.komisyon_sayisi, r.banka_sayisi);
        if (!yazabilir || r.onaylandi) return r.komisyon_odendi ? <Tag color="green">Evet</Tag> : <Tag>Hayır</Tag>;
        return (
          <Switch
            size="small" checkedChildren="Evet" unCheckedChildren="Hayır" checked={r.komisyon_odendi}
            loading={isleniyor === `k${r.bsk_id}`} onChange={(c) => kaydet(r, 'komisyon', { odendi: c }, 'k')}
          />
        );
      },
    },
    {
      title: 'Temsil Tutarı', width: 145, align: 'right',
      render: (_, r) => (r.tip === 'lokasyon'
        ? (r.temsil_tutar ? <Text strong>{para(r.temsil_tutar)}</Text> : null)
        : <TutarGirisi deger={r.temsil_tutar} kilitli={!yazabilir || r.temsil_onaylandi} yukleniyor={isleniyor === `t${r.bsk_id}`}
            onKaydet={(tutar) => kaydet(r, 'temsil', { tutar }, 't')} />),
    },
    {
      title: 'Temsil Onay', width: 100, align: 'center',
      render: (_, r) => {
        if (r.tip === 'lokasyon') return sayac(r.temsil_onay_sayisi, r.banka_sayisi);
        if (r.temsil_onaylandi) {
          return (
            <Tooltip title={`${r.temsil_onaylayan_adi || ''} · ${r.temsil_onay_tarihi ? dayjs(r.temsil_onay_tarihi).format('DD.MM.YYYY HH:mm') : ''}`}>
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
            </Tooltip>
          );
        }
        const tutarYok = r.temsil_tutar == null;
        return (
          <Tooltip title={onayIpucu || (tutarYok ? 'Önce temsil tutarı girilmeli' : '')}>
            <Checkbox checked={false} disabled={!onaylayabilir || tutarYok} onChange={() => temsilOnayla(r, lokasyonu(r.key))} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Onay Tutarı', width: 140, align: 'right',
      render: (_, r) => {
        if (r.tip === 'lokasyon') return r.onay_tutar ? <Text strong>{para(r.onay_tutar)}</Text> : null;
        if (r.onaylandi) return r.onay_tutar == null ? <Text type="secondary">—</Text> : <Text strong>{para(r.onay_tutar)}</Text>;
        if (r.fatura_tutar == null) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title="Henüz onaylanmadı; onaylanınca o anki fatura tutarı kaydedilir">
            <Text type="secondary">{para(r.fatura_tutar)}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Onay', width: 80, align: 'center', fixed: 'right',
      render: (_, r) => {
        if (r.tip === 'lokasyon') return sayac(r.onay_sayisi, r.banka_sayisi);
        if (r.onaylandi) {
          return (
            <Tooltip title={`${r.onaylayan_adi || ''} · ${r.onay_tarihi ? dayjs(r.onay_tarihi).format('DD.MM.YYYY HH:mm') : ''}`}>
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={onayIpucu}>
            <Checkbox checked={false} disabled={!onaylayabilir} onChange={() => onayla(r, lokasyonu(r.key))} />
          </Tooltip>
        );
      },
    },
  ];

  const t = veri?.toplam;
  return (
    <>
      <TurSecimi tur={tur} setTur={setTur} tarihTipi={tarihTipi} />
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap size={[12, 8]}>
          <Segmented options={TARIH_TIPLERI} value={tarihTipi} onChange={setTarihTipi} />
          <RangePicker value={aralik} onChange={setAralik} format="DD.MM.YYYY" allowClear={false}
            presets={[
              { label: 'Bu ay', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
              { label: 'Geçen ay', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
              { label: 'Son 3 ay', value: [dayjs().subtract(2, 'month').startOf('month'), dayjs().endOf('month')] },
              { label: 'Bu yıl', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
            ]}
          />
          <Select allowClear showSearch optionFilterProp="label" placeholder="Sorumlu (tümü)" style={{ width: 200 }}
            value={sorumlu} onChange={setSorumlu} options={sorumlular.map((s) => ({ value: s.id, label: s.ad }))} />
          <Select
            mode="multiple" allowClear placeholder="Tahsilat durumu (tümü)" style={{ minWidth: 200 }}
            maxTagCount="responsive" value={tahsilat} onChange={setTahsilat} options={TAHSILAT_DURUMLARI}
            menuItemSelectedIcon={null}
            optionRender={(o) => <Space><Checkbox checked={tahsilat.includes(o.value)} />{o.label}</Space>}
          />
          <CokluSecim
            placeholder={`Lokasyon (${veri?.lokasyon_secenekleri?.length ?? 0})`}
            secenekler={veri?.lokasyon_secenekleri || []} deger={lokSecim} onChange={setLokSecim}
          />
          <Input.Search allowClear placeholder="Kod, lokasyon, banka, fatura no" style={{ width: 240 }}
            value={ara} onChange={(e) => setAra(e.target.value)} />
          <Button icon={<ClearOutlined />} onClick={sifirla} disabled={varsayilanda}>Filtreleri sıfırla</Button>
          <Tooltip title="Yenile"><Button icon={<ReloadOutlined />} onClick={yukle} /></Tooltip>
          <Button onClick={() => setAcik(acik.length ? [] : (veri?.satirlar || []).filter((s) => s.children).map((s) => s.key))}>
            {acik.length ? 'Tümünü daralt' : 'Tümünü aç'}
          </Button>
          <CiktiButonu onIndir={(bicim) => indir(getAuthHeaders, {
            ...params, satis_turu: tur, tur: 'liste', bicim,
            sorumlu_adi: sorumlular.find((s) => s.id === sorumlu)?.ad,
          }, message)} />
        </Space>
      </Card>

      {t && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          {[
            ['Lokasyon', t.lokasyon, `${t.anlasma} anlaşma · ${t.yeni} yeni satış · ${t.yenileme} yenilenmiş`],
            ['Mülk sözleşme tutarı', para(t.mulk_tutar), `${t.mulk_girdikce} lokasyon girdikçe ödemeli`],
            ['Banka sözleşme tutarı', para(t.toplam_kira), `Ort. ${para(t.ort_kira)}`],
            ['Fatura tutarı', para(t.fatura_tutar), `${t.tahsil_edilen}/${t.faturalanan} tahsil · ${para(t.tahsil_tutar)}`],
            ['Komisyon tutarı', para(t.komisyon_tutar), `${t.komisyon_odenen} ödendi · Temsil ${para(t.temsil_tutar)}`],
            ['Onaylanan', `${t.onaylanan} / ${t.anlasma}`, `${para(t.onay_tutar)} · temsil ${t.temsil_onaylanan} onaylı`],
          ].map(([baslik, deger, alt]) => (
            <Col key={baslik} xs={12} md={8} xl={4} style={{ display: 'flex' }}>
              <Card size="small" style={{ width: '100%' }}>
                <Statistic title={baslik} value={deger} valueStyle={{ fontSize: 18 }} />
                {/* Alt satırı olmayan kutuda da aynı yükseklik kalsın diye boş satır */}
                <Text type="secondary" style={{ fontSize: 12, display: 'block', minHeight: 20 }}>{alt || ' '}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Table
        rowKey="key" size="small" columns={sutunlar} dataSource={gosterilen} loading={yukleniyor}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 500], showTotal: (n) => `${n} lokasyon` }}
        expandable={{ expandedRowKeys: acik, onExpandedRowsChange: (k) => setAcik(k) }}
        rowClassName={(r) => (r.tip === 'lokasyon' ? 'yr-lokasyon' : '')}
        scroll={{ x: 2400 }}
        locale={{ emptyText: <Empty description="Bu aralıkta kayıt yok" /> }}
      />
      {!yazabilir && veri && (
        <Text type="secondary" style={{ display: 'block' }}><LockOutlined /> Yalnız görüntüleme yetkiniz var; komisyon ve temsil tutarı girilemez.</Text>
      )}
      {!onaylayabilir && veri && (
        <Text type="secondary" style={{ display: 'block' }}><LockOutlined /> Onay ve temsil onayını yalnız {veri.onaylayan_adi} verebilir.</Text>
      )}
    </>
  );
}

// --- Rapor --------------------------------------------------------------------------------
function RaporSekmesi({ api, sorumlular }) {
  const { message } = App.useApp();
  const { getAuthHeaders } = useAuth();
  const [tarihTipi, setTarihTipi] = useState('satis');
  const [yil, setYil] = useState(dayjs());
  const [periyot, setPeriyot] = useState(1);
  const [kisiBazli, setKisiBazli] = useState(false);
  const [sorumlu, setSorumlu] = useState();
  const [tur, setTur] = useState('yeni');
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  const params = useMemo(() => ({
    tarih_tipi: tarihTipi, yil: yil?.year(), periyot, kisi_bazli: kisiBazli, sorumlu, tur,
  }), [tarihTipi, yil, periyot, kisiBazli, sorumlu, tur]);

  const sifirla = () => {
    setTarihTipi('satis'); setYil(dayjs()); setPeriyot(1); setKisiBazli(false); setSorumlu(undefined); setTur('yeni');
  };
  const varsayilanda = tarihTipi === 'satis' && yil?.year() === dayjs().year() && periyot === 1 && !kisiBazli
    && !sorumlu && tur === 'yeni';

  useEffect(() => {
    if (!params.yil) return;
    setYukleniyor(true);
    api.get('/rapor', params)
      .then(setVeri)
      .catch((e) => message.error(hata(e)))
      .finally(() => setYukleniyor(false));
  }, [api, params, message]);

  const sayi = (v) => (v ? v.toLocaleString('tr-TR') : <Text type="secondary">0</Text>);
  const sutunlar = [
    { title: kisiBazli ? 'Dönem / Sorumlu' : 'Dönem', dataIndex: 'donem', fixed: 'left', width: 230,
      render: (v, r) => (r.children || r.key === 'T' ? <Text strong>{v}</Text> : v) },
    { title: 'Lokasyon', dataIndex: 'lokasyon', align: 'right', width: 90, render: sayi },
    { title: 'Anlaşma', dataIndex: 'anlasma', align: 'right', width: 90, render: sayi },
    { title: 'Yeni Satış', dataIndex: 'yeni', align: 'right', width: 90, render: sayi },
    { title: 'Yenilenmiş', dataIndex: 'yenileme', align: 'right', width: 100, render: sayi },
    { title: 'Mülk Söz. Tutarı', dataIndex: 'mulk_tutar', align: 'right', width: 150, render: para },
    { title: 'Banka Söz. Tutarı', dataIndex: 'toplam_kira', align: 'right', width: 150, render: para },
    { title: 'Ort. Banka Söz.', dataIndex: 'ort_kira', align: 'right', width: 140, render: para },
    { title: 'Faturalanan', dataIndex: 'faturalanan', align: 'right', width: 100, render: sayi },
    { title: 'Fatura Tutarı', dataIndex: 'fatura_tutar', align: 'right', width: 150, render: para },
    { title: 'Tahsil Edilen', dataIndex: 'tahsil_edilen', align: 'right', width: 110, render: sayi },
    { title: 'Tahsil Tutarı', dataIndex: 'tahsil_tutar', align: 'right', width: 150, render: para },
    { title: 'Komisyon Tutarı', dataIndex: 'komisyon_tutar', align: 'right', width: 140, render: para },
    { title: 'Komisyon Ödenen', dataIndex: 'komisyon_odenen', align: 'right', width: 130, render: sayi },
    { title: 'Temsil Tutarı', dataIndex: 'temsil_tutar', align: 'right', width: 140, render: para },
    { title: 'Temsil Onaylanan', dataIndex: 'temsil_onaylanan', align: 'right', width: 130, render: sayi },
    { title: 'Onaylanan', dataIndex: 'onaylanan', align: 'right', width: 100, render: sayi },
    { title: 'Onay Tutarı', dataIndex: 'onay_tutar', align: 'right', width: 140, render: para },
  ];

  const ozet = (tablo) => (tablo?.toplam ? (
    <Table.Summary fixed>
      <Table.Summary.Row className="yr-toplam">
        {sutunlar.map((s, i) => (
          <Table.Summary.Cell key={s.title} index={i} align={s.align}>
            {i === 0 ? <Text strong>{tablo.toplam.donem}</Text> : <Text strong>{s.render(tablo.toplam[s.dataIndex], tablo.toplam)}</Text>}
          </Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    </Table.Summary>
  ) : null);

  return (
    <>
      <TurSecimi tur={tur} setTur={setTur} tarihTipi={tarihTipi} />
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap size={[12, 8]}>
          <Segmented options={TARIH_TIPLERI} value={tarihTipi} onChange={setTarihTipi} />
          <DatePicker picker="year" value={yil} onChange={setYil} allowClear={false} style={{ width: 100 }} />
          <Segmented options={PERIYOTLAR} value={periyot} onChange={setPeriyot} />
          <Segmented options={[{ value: false, label: 'Genel' }, { value: true, label: 'Kişi bazlı' }]}
            value={kisiBazli} onChange={setKisiBazli} />
          <Select allowClear showSearch optionFilterProp="label" placeholder="Sorumlu (tümü)" style={{ width: 200 }}
            value={sorumlu} onChange={setSorumlu} options={sorumlular.map((s) => ({ value: s.id, label: s.ad }))} />
          <Button icon={<ClearOutlined />} onClick={sifirla} disabled={varsayilanda}>Filtreleri sıfırla</Button>
          <CiktiButonu onIndir={(bicim) => indir(getAuthHeaders, {
            ...params, satis_turu: tur, tur: 'rapor', bicim,
            sorumlu_adi: sorumlular.find((s) => s.id === sorumlu)?.ad,
          }, message)} />
        </Space>
      </Card>
      <Table
        rowKey="key" size="small" columns={sutunlar} dataSource={veri?.donemler || []} loading={yukleniyor}
        pagination={false} scroll={{ x: 2300 }} summary={() => ozet(veri)}
        expandable={{ defaultExpandAllRows: false }}
        rowClassName={(r) => (r.children ? 'yr-lokasyon' : '')}
      />
      {kisiBazli && veri?.kisiler?.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 20 }}>Kişi bazlı yıl toplamı — {params.yil}</Title>
          <Table rowKey="key" size="small" pagination={false} scroll={{ x: 2300 }}
            columns={[{ ...sutunlar[0], title: 'Sorumlu' }, ...sutunlar.slice(1)]} dataSource={veri.kisiler} />
        </>
      )}
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        Yalnız özel mülk lokasyonları (Dükkan, Açık Alan). Dönem, seçilen tarih tipine göre belirlenir; mülk
        sözleşme tarihinde lokasyon sayısına bankası olmayan lokasyonlar da girer. Mülk tutarı = lokasyonun en yeni
        mülk sözleşmesi (lokasyon başına bir kez); banka tutarı = banka sözleşmesi; fatura tutarı = kesilen kira
        faturaları; onay tutarı = onay anındaki fatura tutarı.
      </Text>
    </>
  );
}

export default function YonetimRaporPage() {
  const api = useModuleApi('/yonetim-rapor');
  const [sorumlular, setSorumlular] = useState([]);
  useEffect(() => { api.get('/sorumlular').then(setSorumlular).catch(() => {}); }, [api]);

  return (
    <div className="nf-module yr-sayfa">
      <style>{`
        .nf-module .yr-lokasyon > td { background: rgba(31, 58, 95, 0.06) !important; }
        .nf-module .yr-toplam > td { background: rgba(250, 173, 20, 0.12) !important; }
        /* Başlıklar 2px küçük ve satır kırabilir: dar sütunlarda da tamamı okunsun */
        .yr-sayfa .ant-table-thead > tr > th { font-size: 12px; white-space: normal !important; line-height: 1.25; text-align: center !important; }
      `}</style>
      <Title level={3} style={{ marginTop: 0 }}>
        <SafetyCertificateOutlined style={{ marginRight: 8 }} /> Özel Mülk Satış Rapor
      </Title>
      <Tabs
        type="card"
        items={[
          { key: 'liste', label: 'Liste', children: <ListeSekmesi api={api} sorumlular={sorumlular} /> },
          { key: 'rapor', label: 'Rapor', children: <RaporSekmesi api={api} sorumlular={sorumlular} /> },
        ]}
      />
    </div>
  );
}
